<template>
  <div class="c-option-chart">
    <v-stock-chart
      class="chart chart-bg"
      :options="chartOptions"
      :api="chart"
      theme="dark"
      :height="230"
      ref="stockChart"
    />
  </div>
</template>

<script>
import VStockChart from '@base/VStockChart';

export default {
  name: 'c-option-chart',
  components: {
    VStockChart
  },
  props: {
    chartData: {
      type: Object
    },
    chartType: {
      type: String
    },
    graphName: {
      type: String
    }
  },
  data() {
    return {
      chart: {},
      series: {},
      chartOptions: {
        lang: {
          accessibility: {
            chartContainerLabel: this.getChartTitle(),
            svgContainerLabel: this.getChartTitle(),
            defaultChartTitle: this.getChartTitle()
          }
        },
        accessibility: {
          enabled: false,
          keyboardNavigation: {
            enabled: true,
            seriesNavigation: {
              mode: 'normal'
            }
          },
          series: { pointDescriptionEnabledThreshold: false },
          point: {
            descriptionFormatter: undefined
          },
          screenReaderSection: {}
        },
        chart: {
          zoomType: 'xy',
          styledMode: true,
          marginTop: 5,
          marginBottom: 22,
          padding: 0,
          animation: false
        },
        navigator: {
          enabled: false
        },
        scrollbar: {
          enabled: false
        },
        rangeSelector: {
          enabled: false
        },
        legend: {
          enabled: false
        },
        xAxis: {
          labels: {
            format: '{value:.2f} '
          },
          accessibility: {
            description: this.$t(`white-label.widgets.optionChain.labels.strikePrice`),
            rangeDescription: undefined
          }
        },
        yAxis: {
          labels: {
            align: 'left',
            format: '{value:.2f}'
          },
          startOnTick: false,
          endOnTick: false,
          tickWidth: 1,
          minPadding: 0,
          accessibility: {
            description: undefined,
            rangeDescription: undefined
          }
        },
        plotOptions: {
          series: {
            turboThreshold: 5000,
            connectNulls: true
          },
          line: {
            marker: {
              enabled: true
            }
          }
        },
        tooltip: {
          split: false,
          shared: false,
          enabled: true,
          padding: 8,
          useHTML: false,
          formatter: (vm => {
            return function() {
              const y =
                this.point.fid === 'Opn Int' || this.point.fid === 'Imp Vol' ? this.y.toFixed(0) : this.y.toFixed(3);
              return `<span class="highcharts-series-${this.series.index}">${vm.$t(
                'white-label.widgets.optionChain.graph.controls.expiry'
              )}: ${this.series.name}</span><br/><span>${this.point.fid}: ${y} </span><br/>`;
            };
          })(this)
        }
      },
      graphNameModified: ''
    };
  },
  watch: {
    chartData: {
      handler: 'handleChartData',
      deep: true
    },
    graphName: {
      handler(value) {
        this.graphNameModified = value;
        this.chartOptions.yAxis.accessibility.description = this.graphNameModified;
      }
    },
    '$store.state.applicationSettings.general.settings.applicationLanguage'() {
      this.chartOptions.lang.accessibility.chartContainerLabel = this.getChartTitle();
      this.chartOptions.lang.accessibility.svgContainerLabel = this.getChartTitle();
      this.chartOptions.lang.accessibility.defaultChartTitle = this.getChartTitle();
    }
  },
  methods: {
    resetChart() {
      this.series = {};
      this.chart.removeAllSeries();
    },
    removeSeriesFromChart(seriesID) {
      this.chart.get(seriesID).remove();
      this.series[seriesID] = null;
    },
    handleChartData() {
      Object.keys(this.chartData).forEach(s => {
        if (!this.series[s]) {
          this.series[s] = {
            id: this.chartData[s][0].id,
            name: `${this.chartData[s][0].matIso}`,
            visible: true,
            type: this.chartType,
            colorIndex: this.chartData[s][0].colorIndex,
            index: this.chartData[s][0].colorIndex,
            dataGrouping: {
              enabled: false
            },
            data: this.chartData[s]
          };

          this.chart.addSeries(this.series[s], false);
        } else {
          if (this.chart.get(this.chartData[s][0].id)) {
            this.chart.get(this.chartData[s][0].id).update(
              {
                type: this.chartType,
                data: this.chartData[s].map(v => {
                  return {
                    x: parseFloat(v.x),
                    y: parseFloat(v.y),
                    fid: v.fid
                  };
                })
              },
              false
            );
          }
        }
      });

      this.chart.redraw();

      if (this.$refs.stockChart.chart.xAxis[0].min && this.chartOptions.xAxis.accessibility) {
        this.chartOptions.xAxis.accessibility.rangeDescription =
          'Range:' + this.$refs.stockChart.chart.xAxis[0].min + ' to ' + this.$refs.stockChart.chart.xAxis[0].max;
      }

      if (this.$refs.stockChart.chart.yAxis[0].min && this.chartOptions.yAxis.accessibility) {
        this.chartOptions.yAxis.accessibility.rangeDescription =
          'Range:' +
          this.$f(this.$refs.stockChart.chart.yAxis[0].min, 'float.price') +
          ' to ' +
          this.$f(this.$refs.stockChart.chart.yAxis[0].max, 'float.price');
      }

      this.chartOptions.accessibility.point.descriptionFormatter = this.descriptionFormatter;
    },
    descriptionFormatter(point) {
      const y =
        'Expiry' +
        point.series.name +
        'Strike Price' +
        point.x +
        point.fid +
        (point.fid === 'Opn Int' || point.fid === 'Imp Vol' ? point.y.toFixed(0) : point.y.toFixed(3));
      const output = [];
      output.push(y);

      return output.join(' ');
    },
    getChartTitle() {
      return this.wid
        ? `${this.$t(`white-label.widgets.optionChain.view.optionChainGraph`)} ${this.$t(
            'white-label.globalLabels.chartGraphImage'
          )}`
        : '';
    }
  }
};
</script>

<style lang="scss">
@import '~@/styles/components/custom/c-option-chart';
</style>
